import type { DateValue } from '@internationalized/date';
import { parseDate } from '@internationalized/date';
import { useCallback } from 'react';

import { DatePicker } from '@/lib/base/pickers/date-picker/date-picker';
import { useOrgTimeFormat } from '@/lib/time';

import type { PropertyInputProps } from './TextInput';

export const DateInput: React.FC<PropertyInputProps> = ({
  propertyType,
  value,
  onChange,
  disabled = false,
  autoOpenPicker,
  onPickerOpenChange,
  size = 'sm',
}) => {
  const { hourCycle } = useOrgTimeFormat();
  const stringValue = value as string;
  const dateValue = stringValue ? parseDate(stringValue) : null;

  // Cleared dates emit null — the events API clears a property with a null
  // value; an empty string is rejected by the date field type's validation.
  const handleChange = useCallback(
    (newValue: DateValue | null) =>
      onChange(newValue ? newValue.toString() : null),
    [onChange]
  );

  return (
    <DatePicker
      value={dateValue}
      onChange={handleChange}
      isDisabled={disabled}
      isRequired={propertyType.is_required}
      // Optional dates get a "Clear date" action in the calendar popover —
      // without it a set date could never be removed (the trigger is a
      // button, not a clearable text field).
      clearable={!propertyType.is_required}
      className='w-full'
      // Table-cell editing renders the compact `xs` trigger (matching the
      // other cell editors), a tighter calendar popover, and opens the
      // calendar immediately — entering edit mode IS the intent to pick a date.
      // Every other host gets the caller's `size` (default `sm`).
      size={autoOpenPicker ? 'xs' : size}
      defaultOpen={autoOpenPicker}
      onOpenChange={onPickerOpenChange}
      aria-label={propertyType.label}
      hourCycle={hourCycle}
    />
  );
};
